"use client";

import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Input, Button, Spin, Modal } from "antd";
import { useTranslation } from "react-i18next";

import AppLayout from "../../../navigation/Layout/Layout";
import useMedicationsDatabase from "../../hooks/useMedicationsDatabase";
import "./Medications.css";
import searchImg from "../../../assets/icons/search.png";

/* =========================
   Helpers (safe UI)
========================= */
const safeText = (v, fallback = "—") =>
  v === null || v === undefined || String(v).trim() === "" ? fallback : String(v);

const DEFAULT_MED_AVATAR = `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M10.5 20.5L20.5 10.5C20.9673 10.0421 21.3391 9.49606 21.5941 8.89352C21.849 8.29099 21.982 7.64389 21.9853 6.98965C21.9886 6.33541 21.8622 5.687 21.6133 5.08192C21.3645 4.47684 20.9982 3.92709 20.5355 3.46447C20.0729 3.00184 19.5232 2.63552 18.9181 2.38668C18.313 2.13783 17.6646 2.01141 17.0104 2.01471C16.3561 2.01802 15.709 2.15098 15.1065 2.40593C14.5039 2.66087 13.9579 3.03273 13.5 3.5L3.5 13.5C3.03273 13.9579 2.66087 14.5039 2.40593 15.1065C2.15098 15.709 2.01802 16.3561 2.01471 17.0104C2.01141 17.6646 2.13783 18.313 2.38668 18.9181C2.63552 19.5232 3.00184 20.0729 3.46447 20.5355C3.92709 20.9982 4.47684 21.3645 5.08192 21.6133C5.687 21.8622 6.33541 21.9886 6.98965 21.9853C7.64389 21.982 8.29099 21.849 8.89352 21.5941C9.49606 21.3391 10.0421 20.9673 10.5 20.5Z" stroke="#1e1e1e" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.5 8.5L15.5 15.5" stroke="#1e1e1e" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>')}`;

const safeImg = (m) => {
  const url = m?.image_url;
  if (
    !url ||
    String(url).trim() === "" ||
    url === "image_url" ||          // valeur placeholder backend
    !url.startsWith("http")         // pas une vraie URL
  ) {
    return DEFAULT_MED_AVATAR;
  }
  return url;
};

export default function Medications() {
  const { t, i18n } = useTranslation();

  // ✅ Hook pour consommer l'API
  const {
    medications,
    searchResults,
    loading,
    searchLoading,
    hasSearchResults,
    loadMedications,
    search,
    clearSearch,
  } = useMedicationsDatabase();

  const isRTL = i18n.language?.startsWith("ar");

  // ✅ direction RTL on the page (optional but recommended)
  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [isRTL]);

  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeout = React.useRef(null);
  const dropdownRef = React.useRef(null);

  // ✅ Charger les médicaments au montage du composant
  useEffect(() => {
    loadMedications({ page: 1, limit: 20 });
  }, [loadMedications]);

  // ✅ Décider quelles données afficher
  const displayedData = useMemo(() => {
    return showSearchResults ? searchResults : medications;
  }, [showSearchResults, searchResults, medications]);

  const safeName = useCallback(
    (m) => safeText(m?.dci || m?.nom_commercial || m?.name || m?.denomination, t("meds.unknownMedication")),
    [t]
  );

  // ✅ Afficher la forme du médicament
  const safeForm = useCallback(
    (m) => {
      // Backend renvoie directement le texte dans 'forme'
      return safeText(m?.forme || m?.form, "—");
    },
    []
  );

  // ✅ Format price
  const formatPrice = useCallback(
    (m) => {
      // Backend utilise 'prix_public' qui peut être "Non précisé" ou un nombre avec " DT"
      const prixPublic = m?.prix_public || m?.price;
      
      if (!prixPublic || prixPublic === "Non précisé") return "—";
      
      // Si c'est déjà une string avec "DT", la retourner telle quelle
      if (typeof prixPublic === 'string') {
        return prixPublic;
      }
      
      // Sinon, convertir en nombre
      const price = Number(prixPublic);
      if (isNaN(price)) return "—";
      
      const currency = t("meds.currency", { defaultValue: "DT" });
      return `${price.toFixed(3)} ${currency}`;
    },
    [t]
  );

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (val.trim() === "") {
      setShowSearchResults(false);
      clearSearch();
      setShowDropdown(false);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setIsSearching(true);
      await search({ q: val.trim(), page: 1, limit: 20 });
      setShowDropdown(true);
      setIsSearching(false);
    }, 300);
  };

  const handleSearch = async () => {
    const q = query.trim();
    if (!q) {
      setShowSearchResults(false);
      clearSearch();
      setShowDropdown(false);
      return;
    }
    setIsSearching(true);
    await search({ q, page: 1, limit: 20 });
    setShowSearchResults(true);
    setShowDropdown(false);
    setIsSearching(false);
  };
  // Filter search results locally to ensure they match the search term
  const filteredDropdownResults = useMemo(() => {
    if (!query.trim()) return [];
    return searchResults.filter((m) =>
      safeName(m).toLowerCase().includes(query.trim().toLowerCase())
    );
  }, [searchResults, query, safeName]);

  // ✅ État affiché à l'écran
  const filtered = useMemo(() => displayedData, [displayedData]);

  // ✅ Empty shown when searching and no results
  const showEmpty = showSearchResults && filtered.length === 0 && !searchLoading;

  // ✅ Can search when query not empty
  const canSearch = useMemo(() => query.trim().length > 0, [query]);

  const handleUseMedication = (med) => {
    setSelectedMedication(med);
    setIsModalVisible(true);
  };

  const handleViewNotice = (med) => {
    setSelectedMedication(med);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedMedication(null);
  };

  return (
    <AppLayout>
      <div className={`meds-page ${isRTL ? "is-rtl" : ""}`}>
        <div className="meds-card">
          <div className="meds-head">
            <h2 className="meds-title">{t("meds.title")}</h2>
            <p className="meds-subtitle">{t("meds.subtitle")}</p>
          </div>

          <div className="meds-search" style={{ position: 'relative' }}>
            <Input
              className="meds-input"
              placeholder={t("meds.placeholder")}
              value={query}
              onChange={handleQueryChange}
              allowClear
              onPressEnter={handleSearch}
              autoComplete="off"
            />
            <Button
              className="meds-btn"
              type="primary"
              loading={isSearching || searchLoading}
              disabled={!canSearch}
              onClick={handleSearch}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M17.5003 17.5003L13.762 13.7553M15.8337 8.75033C15.8337 10.6289 15.0874 12.4306 13.759 13.759C12.4306 15.0874 10.6289 15.8337 8.75033 15.8337C6.87171 15.8337 5.07004 15.0874 3.74165 13.759C2.41327 12.4306 1.66699 10.6289 1.66699 8.75033C1.66699 6.87171 2.41327 5.07004 3.74165 3.74165C5.07004 2.41327 6.87171 1.66699 8.75033 1.66699C10.6289 1.66699 12.4306 2.41327 13.759 3.74165C15.0874 5.07004 15.8337 6.87171 15.8337 8.75033Z"
                  stroke="white"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                />
              </svg>
              {t("meds.search")}
            </Button>
            {showDropdown && (
              <div
                ref={dropdownRef}
                style={{
                  position: 'absolute',
                  top: 40,
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  background: '#fff',
                  border: '1px solid #eee',
                  borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  maxHeight: 300,
                  overflowY: 'auto',
                }}
              >
                {filteredDropdownResults.length > 0 ? (
                  filteredDropdownResults.map((m, idx) => (
                    <div
                      key={m._id || m.id || idx}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' }}
                      onClick={() => {
                        setSelectedMedication(m);
                        setIsModalVisible(true);
                        setShowDropdown(false);
                        setQuery(safeName(m));
                      }}
                    >
                      <img src={safeImg(m)} alt={safeName(m)} style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee', marginRight: 8 }} />
                      <span>{safeName(m)}</span>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '12px 16px', color: '#888', textAlign: 'center' }}>
                    {t('meds.noMedicationFound', { defaultValue: 'Aucun médicament trouvé' })}
                  </div>
                )}
              </div>
            )}
          </div>

          {loading && !showSearchResults ? (
            <div className="meds-body" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
              <Spin size="large" tip={t("meds.loading", { defaultValue: "Chargement..." })} />
            </div>
          ) : showEmpty ? (
            <div className="meds-body">
              <EmptyIllustration img={searchImg} alt={t("meds.emptyAlt")}
                message={t("meds.noMedicationFound")} />
            </div>
          ) : (
            <div className="meds-listWrap">
              <div className="meds-list">
                {filtered.map((m, idx) => (
                  <div className="meds-row" key={m._id || m.id || idx}>
                    <div className="meds-leftt">
                      <img
                        className="meds-img"
                        src={safeImg(m)}
                        alt={safeName(m)}
                        loading="lazy"
                      />
                      <div className="meds-name">{safeName(m)}</div>
                    </div>
                    <div className="meds-form">{safeForm(m)}</div>
                    <div className="meds-price">{formatPrice(m)}</div>
                    <div className="meds-actions">
                  
                      <button
                        className="meds-noticeBtn"
                        type="button"
                        onClick={() => handleViewNotice(m)}
                      >
                        {t("meds.viewNotice")}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M9.99935 3.33366C13.6752 3.33366 16.666 6.32449 16.666 10.0003C16.666 13.6762 13.6752 16.667 9.99935 16.667C6.32352 16.667 3.33268 13.6762 3.33268 10.0003C3.33268 6.32449 6.32352 3.33366 9.99935 3.33366ZM9.99935 1.66699C5.39685 1.66699 1.66602 5.39783 1.66602 10.0003C1.66602 14.6028 5.39685 18.3337 9.99935 18.3337C14.6018 18.3337 18.3327 14.6028 18.3327 10.0003C18.3327 5.39783 14.6018 1.66699 9.99935 1.66699ZM10.8327 12.5003H9.16602V14.167H10.8327V12.5003ZM9.16602 10.8337H10.8327L11.2493 5.83366H8.74935L9.16602 10.8337Z"
                            fill="#1e1e1e"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <Modal
          title={selectedMedication?.nom_commercial || t("meds.medicationDetails", { defaultValue: "Détails du médicament" })}
          open={isModalVisible}
          onCancel={handleCloseModal}
          footer={[
            <Button key="close" onClick={handleCloseModal}>
              {t("meds.close", { defaultValue: "Fermer" })}
            </Button>,
          ]}
          width={700}
          className="medication-details-modal"
        >
          {selectedMedication && (
            <div className="medication-details">
              <div className="detail-section">
                {selectedMedication.image_url && (
                  <div className="detail-image-wrapper">
                    <img
                      src={selectedMedication.image_url}
                      alt={selectedMedication.nom_commercial}
                      className="detail-image"
                      onError={(e) => { e.target.onerror = null; e.target.src = DEFAULT_MED_AVATAR; }}
                    />
                  </div>
                )}
              </div>
              <div className="detail-info">
                <div className="detail-row">
                  <strong>{t("meds.details.dci", { defaultValue: "DCI" })}:</strong>
                  <span>{safeText(selectedMedication.dci)}</span>
                </div>
                <div className="detail-row">
                  <strong>{t("meds.details.forme", { defaultValue: "Forme" })}:</strong>
                  <span>{safeText(selectedMedication.forme)}</span>
                </div>
                <div className="detail-row">
                  <strong>{t("meds.details.dosage", { defaultValue: "Dosage" })}:</strong>
                  <span>{safeText(selectedMedication.dosage)}</span>
                </div>
                <div className="detail-row">
                  <strong>{t("meds.details.laboratoire", { defaultValue: "Laboratoire" })}:</strong>
                  <span>{safeText(selectedMedication.laboratoire)}</span>
                </div>
                <div className="detail-row">
                  <strong>{t("meds.details.classeTherapeutique", { defaultValue: "Classe thérapeutique" })}:</strong>
                  <span>{safeText(selectedMedication.classe_therapeutique)}</span>
                </div>
                <div className="detail-row">
                  <strong>{t("meds.details.presentation", { defaultValue: "Présentation" })}:</strong>
                  <span>{safeText(selectedMedication.presentation)}</span>
                </div>
                <div className="detail-row">
                  <strong>{t("meds.details.conditionnement", { defaultValue: "Conditionnement" })}:</strong>
                  <span>{safeText(selectedMedication.conditionnement)}</span>
                </div>
                <div className="detail-row">
                  <strong>{t("meds.details.price", { defaultValue: "Prix public" })}:</strong>
                  <span>{formatPrice(selectedMedication)}</span>
                </div>
                {selectedMedication.description_courte && (
                  <div className="detail-row detail-description">
                    <strong>{t("meds.details.description", { defaultValue: "Description" })}:</strong>
                    <p>{selectedMedication.description_courte}</p>
                  </div>
                )}
                {selectedMedication.duree_conservation && selectedMedication.duree_conservation !== "Non précisé" && (
                  <div className="detail-row">
                    <strong>{t("meds.details.conservation", { defaultValue: "Durée de conservation" })}:</strong>
                    <span>{selectedMedication.duree_conservation} </span>
                  </div>
                )}
               
              </div>
            </div>
          )}
        </Modal>
      </div>
    </AppLayout>
  );
}

/* =========================
   Empty illustration
========================= */
function EmptyIllustration({ img, alt, message }) {
  return (
    <div className="meds-empty">
      <img src={img} alt={alt} className="meds-empty__svg" loading="lazy" />
      <div className="meds-empty__text">{message}</div>
    </div>
  );
}